import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, Restaurant, MenuItem } from '../lib/supabase';
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  ChevronLeft,
  UtensilsCrossed,
} from 'lucide-react';

type SizeOption = 'liten' | 'medium' | 'stor';
type MenuItemPrice = { size: SizeOption; price: number };

type MenuItemWithPrices = MenuItem & {
  menu_item_prices?: MenuItemPrice[];
};

type CartItem = {
  menuItem: MenuItemWithPrices;
  size: SizeOption;
  unitPrice: number;
  quantity: number;
};

export default function RestaurantMenuPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItemWithPrices[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<Record<string, SizeOption>>({});
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    if (restaurantId) {
      loadData(restaurantId);
    }
  }, [restaurantId]);

  const loadData = async (id: string) => {
    setLoading(true);

    const [restaurantRes, menuRes] = await Promise.all([
      supabase.from('restaurants').select('*').eq('id', id).maybeSingle(),
      supabase
        .from('menu_items')
        .select('*, menu_item_prices(size, price)')
        .eq('restaurant_id', id)
        .eq('is_available', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true }),
    ]);

    if (restaurantRes.data) {
      setRestaurant(restaurantRes.data as Restaurant);
    }

    if (menuRes.data) {
      const items = menuRes.data as MenuItemWithPrices[];
      setMenuItems(items);

      const defaults: Record<string, SizeOption> = {};
      for (const it of items) {
        const sizes = (it.menu_item_prices || []).map((s) => s.size);
        if (sizes.length === 0) {
          defaults[it.id] = 'medium';
        } else {
          defaults[it.id] = (sizes.includes('medium') ? 'medium' : sizes[0]) as SizeOption;
        }
      }
      setSelectedSizes(defaults);
    }

    setLoading(false);
  };

  const priceFor = (item: MenuItemWithPrices, size: SizeOption): number => {
    const p = item.menu_item_prices?.find((m) => m.size === size)?.price;
    return typeof p === 'number' ? p : item.price;
  };

  const addToCart = (item: MenuItemWithPrices) => {
    const size = selectedSizes[item.id] ?? 'medium';
    const unitPrice = priceFor(item, size);
    const key = `${item.id}-${size}`;
    const existingIndex = cart.findIndex((ci) => `${ci.menuItem.id}-${ci.size}` === key);

    if (existingIndex >= 0) {
      const next = [...cart];
      next[existingIndex] = { ...next[existingIndex], quantity: next[existingIndex].quantity + 1 };
      setCart(next);
    } else {
      setCart((prev) => [...prev, { menuItem: item, size, unitPrice, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemId: string, size: SizeOption, change: number) => {
    const key = `${itemId}-${size}`;
    setCart((prev) =>
      prev
        .map((ci) =>
          `${ci.menuItem.id}-${ci.size}` === key
            ? { ...ci, quantity: Math.max(0, ci.quantity + change) }
            : ci
        )
        .filter((ci) => ci.quantity > 0)
    );
  };

  const removeFromCart = (itemId: string, size: SizeOption) => {
    setCart((prev) => prev.filter((ci) => !(ci.menuItem.id === itemId && ci.size === size)));
  };

  const total = cart.reduce((sum, ci) => sum + ci.unitPrice * ci.quantity, 0);
  const vaerCoins = total * 0.01;
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);

  const grouped = useMemo(() => {
    return menuItems.reduce((acc, item) => {
      const cat = item.category || 'Annet';
      (acc[cat] ||= []).push(item);
      return acc;
    }, {} as Record<string, MenuItemWithPrices[]>);
  }, [menuItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 dark:text-gray-400">
        Laster...
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <UtensilsCrossed className="w-16 h-16 text-gray-300 dark:text-gray-700 mb-4" />
        <p className="text-gray-500 dark:text-gray-400 mb-4">Restaurant ikke funnet</p>
        <button
          onClick={() => navigate('/food')}
          className="px-6 py-3 bg-[#004D99] hover:bg-[#003366] text-white rounded-xl font-semibold"
        >
          Tilbake til restauranter
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-24">
      <div className="sticky top-14 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-800 px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/food')}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          {restaurant.logo_url ? (
            <img
              src={restaurant.logo_url}
              alt={restaurant.name}
              className="w-10 h-10 object-contain rounded-lg"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-[#004D99] dark:text-orange-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
              {restaurant.name}
            </h1>
            {restaurant.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {restaurant.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{cat}</h2>
            <div className="space-y-3">
              {items.map((item) => {
                const size = selectedSizes[item.id] ?? 'medium';
                const sizes = item.menu_item_prices?.map((s) => s.size) || [];
                const price = priceFor(item, size);
                const hasVariants = item.menu_item_prices && item.menu_item_prices.length > 1;

                return (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm"
                  >
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {item.description}
                          </p>
                        )}

                        {hasVariants && (
                          <select
                            value={size}
                            onChange={(e) =>
                              setSelectedSizes((prev) => ({
                                ...prev,
                                [item.id]: e.target.value as SizeOption,
                              }))
                            }
                            className="mb-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm"
                          >
                            {sizes.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-[#004D99] dark:text-orange-400">
                            {price} kr
                          </span>
                          <button
                            onClick={() => addToCart(item)}
                            className="px-4 py-2 bg-[#004D99] hover:bg-[#003366] text-white rounded-lg font-semibold active:scale-95 transition-transform"
                          >
                            Legg til
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {cartCount > 0 && (
        <div className="fixed bottom-20 left-4 right-4 z-40">
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-[#004D99] hover:bg-[#003366] text-white rounded-2xl px-6 py-4 shadow-lg flex items-center justify-between font-semibold active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span>Vis handlekurv</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-sm">
                {cartCount}
              </span>
              <span>{total.toFixed(2)} kr</span>
            </div>
          </button>
        </div>
      )}

      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowCart(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Handlekurv</h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-4 mb-6">
                {cart.map((ci) => (
                  <div
                    key={`${ci.menuItem.id}-${ci.size}`}
                    className="flex items-start gap-3 pb-4 border-b border-gray-200 dark:border-gray-700 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {ci.menuItem.name}
                        {ci.menuItem.menu_item_prices && ci.menuItem.menu_item_prices.length > 1 ? (
                          <span className="text-xs text-gray-500"> ({ci.size})</span>
                        ) : null}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {ci.unitPrice} kr
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(ci.menuItem.id, ci.size, -1)}
                        className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium text-gray-900 dark:text-white">
                        {ci.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(ci.menuItem.id, ci.size, 1)}
                        className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(ci.menuItem.id, ci.size)}
                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-gray-900 dark:text-white">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-bold">{total.toFixed(2)} kr</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>VærCoins (1%)</span>
                  <span className="font-medium">+{vaerCoins.toFixed(2)}</span>
                </div>
              </div>

              <button className="w-full py-4 bg-[#004D99] hover:bg-[#003366] text-white font-bold rounded-xl transition-colors">
                Gå til kassen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
